import {useEffect, useState} from 'react';
import StageDistributionGridView from "../components/StageDistributionGridView";
import StageProgressChartView from "../components/StageProgressChartView";
import LessonHistoryChartView from "../components/LessonHistoryChartView";
import NavigationBarView from "../../navbar/components/NavigationBarView";
import StatsView, {defaultStats} from "../components/StatsView";
import WordListSimpleView from "../components/WordListSimpleView";
import ReviewForecastListView from "../components/ReviewForecastListView";
import SplitFloatingActionButtonView from "../components/SplitFloatingActionButtonView";
import {Box, Grid} from "@mui/material";
import {LessonHistory, ReviewForecast, SrsStageDistribution, SrsStages, Stats} from "../types/stats";
import {BasicWordWithIdList} from "../../../common/types/words";
import {convertDashboard} from "../utils/DashboardConverter";
import {LoadingPageView} from "../../../common/components/LoadingPageView";
import {useApiRequest} from "../../../common/hooks/useApiRequest";
import LogoErrorView from "../../../common/components/LogoErrorView";
import {useDeck} from "../../deck-management/context/DeckContext";
import {useQuery} from "@tanstack/react-query";
import {theme} from "../../../app/theme";

function DashboardView() {


    const [lessonCount, setLessonCount] = useState<number>(0);
    const [reviewCount, setReviewCount] = useState<number>(0);
    const [srsStageCounts, setSrsStageCounts] = useState<SrsStages>(new Map());
    const [srsProgressData, setSrsProgressData] = useState<SrsStageDistribution>(new Map());
    const [lessonVolumeHistory, setLessonVolumeHistory] = useState<LessonHistory>(new Map());
    const [statsInfo, setStatsInfo] = useState<Stats>(defaultStats);
    const [recentLessonsList, setRecentLessonsList] = useState<BasicWordWithIdList>([]);
    const [recentMistakesList, setRecentMistakesList] = useState<BasicWordWithIdList>([]);
    const [frequentMistakesList, setFrequentMistakesList] = useState<BasicWordWithIdList>([]);
    const [upcomingLessonsList, setUpcomingLessonsList] = useState<BasicWordWithIdList>([]);
    const [reviewForecast, setReviewForecast] = useState<ReviewForecast>([]);
    const [dashBoardDataReady, setDashboardDataReady] = useState<boolean>(false);
    
    const {currentDeck, ready : deckDataReady, error : deckLoadError} = useDeck();

    const {getRequest} = useApiRequest();

    const {data: dashboardData, error: dashboardError} = useQuery({
        queryKey: ['dashboard', currentDeck?.deckId],
        queryFn: () => getRequest(`decks/${currentDeck?.deckId}/dashboard`, convertDashboard),
        enabled: deckDataReady,
    })
    

    useEffect(() => {
        if (!dashboardData)
            return;

        const {lessons, reviews, reviewForecast, srsStages, srsStageProgress, lessonHistory, stats, recentLessons, upcomingLessons, recentMistakes, topMistakes} = dashboardData;
        setLessonCount(lessons);
        setReviewCount(reviews);
        setSrsStageCounts(srsStages);
        setSrsProgressData(srsStageProgress);
        setLessonVolumeHistory(lessonHistory);
        setStatsInfo(stats);
        setRecentLessonsList(recentLessons);
        setUpcomingLessonsList(upcomingLessons);
        setRecentMistakesList(recentMistakes);
        setFrequentMistakesList(topMistakes);
        setReviewForecast(reviewForecast);
        setDashboardDataReady(true);
    }, [dashboardData]);

    if (deckLoadError || dashboardError) {
        return (
            <LogoErrorView severity={"error"} message={"An error occurred"}/>
        )
    }

    if (!dashBoardDataReady) {
        return (
            <LoadingPageView/>
        );
    }

    return (
        <Box sx={{width: '100%', bgcolor: theme.palette.background.default}}>
            <NavigationBarView displayDeckSelectionDropdown={true} displaySearchBar={true} displayVocabularyLink={true}/>
            <Box sx={{maxWidth: 1400, m: '0 auto', mt: 5}} px={{xs:1, sm:2, md: 0}}>
                <Grid container spacing={3} sx={{mb: 2}}>
                    <Grid item lg={9} xs={12}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <StatsView stats={statsInfo} ready={true}/>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {chartsView()}
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item lg={3} xs={12}>
                        <ReviewForecastListView forecast={reviewForecast} initialReviews={reviewCount} ready={true}/>
                    </Grid>
                </Grid>
                {wordListsView()}
            </Box>
            <SplitFloatingActionButtonView reviewCount={reviewCount} lessonCount={lessonCount}/>
        </Box>

    );

    function chartsView() {
        return (
            <Box>
                <Grid container spacing={2}>
                    <Grid item sm={12} xs={12}>
                        <StageDistributionGridView stages={srsStageCounts} ready={true}/>
                    </Grid>
                    <Grid item sm={12} xs={12}>
                        <StageProgressChartView stages={srsProgressData} ready={true}/>
                    </Grid>
                    <Grid item sm={12} xs={12}>
                        <LessonHistoryChartView historyMap={lessonVolumeHistory} ready={true} />
                    </Grid>
                </Grid>
            </Box>
        )
    }

    function wordListsView() {
        return (
            <Box sx={{my: 2}}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}><WordListSimpleView title={"Recent Lessons"} words={recentLessonsList} ready={true}/></Grid>
                    <Grid item xs={12} sm={6} md={3}><WordListSimpleView title={"Upcoming Lessons"} words={upcomingLessonsList} ready={true}/></Grid>
                    <Grid item xs={12} sm={6} md={3}><WordListSimpleView title={"Recent Mistakes"} words={recentMistakesList} ready={true}/></Grid>
                    <Grid item xs={12} sm={6} md={3}><WordListSimpleView title={"Top Mistakes"} words={frequentMistakesList} ready={true}/></Grid>
                </Grid>
            </Box>
        )
    }



}

export default DashboardView;
